import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import PromoBanner from '@wix/da-ds/pkg/PromoBanner';
import TierBadge from '@wix/da-ds/pkg/Badges/TierBadge';
import EmptyState from '../../../EmptyState';
import { URLS } from '@wix/da-url/dist/src/constants';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import TierCreatorReduxModule from '@wix/da-shared-react/pkg/Tier/redux/creator/ModuleLoadable';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import s from './SubscriptionsCoreOnlyBlock.scss';

export interface Props {
  className?: string;
  hasCore: boolean;
  createTier: () => void;
  createTipJar: () => void;
}

const SubscriptionsCoreOnlyBlock: React.FC<Props> = ({
  className,
  hasCore,
  createTier,
  createTipJar,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const withTipJar = useFlag('tip_jar');

  let variant;
  let cta;
  let secondaryCta;
  // eslint-disable-next-line default-case
  switch (true) {
    case hasCore:
      variant = 'hasCore';
      cta = (
        <SolidButton
          variant="approval-green"
          size="medium"
          priority="primary"
          onClick={createTier}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.createTier')}
        </SolidButton>
      );
      secondaryCta = (
        <>
          {withTipJar && (
            <GhostButton
              variant="main"
              size="medium"
              onClick={createTipJar}
              className={s['button']}
              biData={{
                evid: BiEventType.TIER_ADD_NEW,
                is_empty_state_view: 1,
                is_tip_jar: 1,
                widgetname: undefined,
              }}
            >
              {t('page.subscriptions.coreOnlyBlock.CTAbutton.createTipJar')}
            </GhostButton>
          )}
        </>
      );
      break;
    case !hasCore:
      variant = 'noCore';
      cta = (
        <BrandedButton
          size="medium"
          variant="core-orange"
          href={URLS.CORE_MEMBERSHIP}
          className={s['button']}
          biData={{
            evid: BiEventType.BUY_CORE_BUTTON,
            referral_info: ReferralType.SUBSCRIPTIONS,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.buyCore')}
        </BrandedButton>
      );
      secondaryCta = (
        <GhostButton
          variant="main"
          size="medium"
          onClick={createTier}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.startCreatingATier')}
        </GhostButton>
      );
      break;
  }

  return (
    <div className={s['root']}>
      <TierCreatorReduxModule />
      {!hasCore && withTipJar && (
        <PromoBanner
          className={s['promo-strip']}
          icon={<TierBadge size={24} />}
          size={isMobile ? 'small' : 'big'}
          variant="subscription"
          messageAfterIcon={t('page.subscriptions.promoStrip.tipJar.message')}
          ctaLabel={t('page.subscriptions.promoStrip.tipJar.CTAbutton')}
          ctaOnClick={createTipJar}
          ctaBiData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            is_tip_jar: 1,
            widgetname: undefined,
          }}
        />
      )}
      <EmptyState
        className={c(s['empty-state-block'], className)}
        title={t(
          `page.subscriptions.coreOnlyBlock.title.var.${variant}.updatedTiers`
        )}
        subtitle={
          <div className={s['subtitle-wrapper']}>
            <div className={s['subtitle']}>
              {t(
                `page.subscriptions.coreOnlyBlock.subtitle.var.${variant}.updatedTiers`
              )}
            </div>
            {hasCore && (
              <TextButton
                size="medium"
                variant="main"
                href={t('widgets.tierContent.learnMore.url')}
              >
                {t('page.subscriptions.coreOnlyBlock.CTAbutton.learnMore')}
              </TextButton>
            )}
          </div>
        }
        illustration={<div className={s['illustration']} />}
        withIllustrationBelow
        customButton={cta}
        customSecondaryButton={secondaryCta}
      />
    </div>
  );
};
SubscriptionsCoreOnlyBlock.displayName = 'SubscriptionsCoreOnlyBlock';

export default SubscriptionsCoreOnlyBlock;
